import { useRef, useState } from "react";
import { Button, Modal, Spinner } from "react-bootstrap";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { useAppSelector } from "Redux/hooks";
import { duplicateOrder } from "Services/order";

export function useOrderDuplication(refresh: () => Promise<any>) {
  const { t } = useTranslation("myOrder");
  const pending = useRef(false);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [duplicateOrderId, setDuplicateOrderId] = useState<number | null>(null);
  const [duplicateOrderNumber, setDuplicateOrderNumber] = useState("");
  const closeDuplicateModal = () => {
    setDuplicateOrderId(null);
    setDuplicateOrderNumber("");
  };
  const duplicate = async (orderId: number) => {
    if (pending.current) return;
    pending.current = true;
    setIsDuplicating(true);
    try {
      const response = await duplicateOrder(orderId);
      if (!response?.data?.id || !response.data.order_no) {
        throw new Error("Incomplete duplication response");
      }
      setDuplicateOrderId(response.data.id);
      setDuplicateOrderNumber(response.data.order_no);
      try {
        await refresh();
      } catch (error) {
        toast.error(t("duplicate refresh failed"));
      }
    } catch (error) {
      toast.error(t("duplicate failed"));
    } finally {
      pending.current = false;
      setIsDuplicating(false);
    }
  };
  return { duplicate, isDuplicating, duplicateOrderId, duplicateOrderNumber,
    showDuplicateSuccessModal: duplicateOrderId !== null, closeDuplicateModal };
}

export function DuplicateOrderModals({ isDuplicating, showDuplicateSuccessModal,
  duplicateOrderId, duplicateOrderNumber, closeDuplicateModal }: ReturnType<typeof useOrderDuplication>) {
  const { t } = useTranslation("myOrder");
  const rightToLeft = useAppSelector(state => state.root.rightToLeft);
  const history = useHistory();
  return <>
      <Modal
        show={isDuplicating}
        centered
        backdrop="static"
        keyboard={false}
        dialogClassName="duplicate-order-loading-modal"
        contentClassName="duplicate-order-loading-modal__content"
      >
        <Modal.Body
          className="duplicate-order-loading-modal__body"
          dir={rightToLeft ? "rtl" : "ltr"}
        >
          <Spinner
            animation="border"
            role="status"
            className="duplicate-order-loading-modal__spinner"
          >
            <span className="sr-only">Loading...</span>
          </Spinner>

          <h4>{t("duplicating order")}</h4>

          <p>
            {t("duplicate wait")}
          </p>
        </Modal.Body>
      </Modal>
      <Modal
        show={showDuplicateSuccessModal}
        onHide={closeDuplicateModal}
        centered
        backdrop="static"
        dialogClassName="duplicate-order-success-modal"
        contentClassName="duplicate-order-success-modal__content"
      >
        <div
          className="duplicate-order-success-modal__wrapper"
          dir={rightToLeft ? "rtl" : "ltr"}
        >
          <Modal.Header closeButton>
            <div className="duplicate-order-success-modal__header-content">
              <div className="duplicate-order-success-modal__success-icon">
                ✓
              </div>

              <Modal.Title>{t("duplicate success")}</Modal.Title>
            </div>
          </Modal.Header>

          <Modal.Body>
            <p className="duplicate-order-success-modal__message">
              {t("duplicate success description")}
            </p>

            {duplicateOrderNumber && (
              <div className="duplicate-order-success-modal__order-number">
                <span>{t("new order number")}</span>
                <strong>{duplicateOrderNumber}</strong>
              </div>
            )}
          </Modal.Body>

          <Modal.Footer>
            <Button
              className="duplicate-order-success-modal__secondary-button"
              onClick={closeDuplicateModal}
            >
              {t("stay in orders")}
            </Button>

            <Button
              className="duplicate-order-success-modal__primary-button"
              disabled={!duplicateOrderId}
              onClick={() => {
                if (!duplicateOrderId) {
                  return;
                }

                const orderId = duplicateOrderId;

                closeDuplicateModal();
                history.push(`/edit-order/${orderId}`);
              }}
            >
              {t("edit duplicated order")}
            </Button>
          </Modal.Footer>
        </div>
      </Modal>
  </>;
}
